import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { Link } from "@/components/link";

export type SafetyGraphItem = {
  label: string;
  value: number;
  fill?: string | { from: string; to: string };
  // Destination for a <Link> to this item's detail view (e.g. filtered by risk type) —
  // `to` built from useScopedUrl() + the desired path. Omit for a non-clickable row.
  link?: { to: string; search?: Record<string, unknown> };
};

type SafetyGraphProps = {
  data: SafetyGraphItem[];
  // Rows always render up to this count, padded with empty bars — keeps the
  // card's height constant instead of shrinking/growing with fewer results.
  minRows?: number;
};

export const SafetyGraph = ({ data, minRows = 5 }: SafetyGraphProps) => {
  const max = Math.max(0, ...data.map((item) => item.value));
  const paddedRows = Math.max(0, minRows - data.length);

  return (
    <div className="flex flex-col gap-2 w-full">
      {data.map((item) => (
        <div key={item.label} className="flex items-center gap-3 h-6 w-full">
          <Tooltip title={item.label}>
            <TooltipTrigger className="inline-block w-[140px] shrink-0 text-left">
              {item.link ? (
                <Link {...item.link} className="block text-xs truncate text-primary underline decoration-dotted">
                  {item.label}
                </Link>
              ) : (
                <p className="text-xs truncate text-primary">{item.label}</p>
              )}
            </TooltipTrigger>
          </Tooltip>
          <div className="relative flex-1 h-full min-w-0 rounded overflow-hidden bg-white">
            <div
              className="absolute inset-y-0 left-0 rounded"
              style={{
                width: `${max > 0 ? (item.value / max) * 100 : 0}%`,
                background: "linear-gradient(90deg, #D5A9FF 0%, #FFBF8B 100%)",
              }}
            />
          </div>
          <p className="text-xs w-[36px] text-right shrink-0 text-primary">{item.value}</p>
        </div>
      ))}
      {Array.from({ length: paddedRows }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed-count empty placeholder rows
        <div key={`empty-${i}`} className="flex items-center gap-3 h-6 w-full">
          <div className="w-[140px] shrink-0" />
          <div className="relative flex-1 h-full min-w-0 rounded overflow-hidden bg-white" />
          <div className="w-[36px] shrink-0" />
        </div>
      ))}
    </div>
  );
};
